/**
 * Biomarker educational content — descriptions and source attributions
 * keyed by LOINC code. Used on Trends detail screens to explain what each
 * biomarker measures and why it matters.
 */

export interface BiomarkerContent {
  description: string;
  source: string;
  sourceUrl?: string;
}

export const BIOMARKER_CONTENT: Record<string, BiomarkerContent> = {
  // ── Metabolism / Lipids ──────────────────────────────────────────────────
  "2093-3": {
    description:
      "Total cholesterol measures all cholesterol in the blood. High levels increase the risk of heart disease and stroke.",
    source: "American Heart Association",
    sourceUrl: "https://www.heart.org/en/health-topics/cholesterol",
  },
  "2085-9": {
    description:
      "HDL cholesterol helps remove other forms of cholesterol from the bloodstream. Higher levels are protective.",
    source: "American Heart Association",
    sourceUrl: "https://www.heart.org/en/health-topics/cholesterol",
  },
  "2089-1": {
    description:
      "LDL cholesterol builds up in artery walls, leading to plaque formation. Lower levels reduce cardiovascular risk.",
    source: "American Heart Association",
    sourceUrl: "https://www.heart.org/en/health-topics/cholesterol",
  },
  "2571-8": {
    description:
      "Triglycerides are fats in the blood used for energy. Elevated levels are linked to heart disease and metabolic syndrome.",
    source: "Mayo Clinic",
    sourceUrl: "https://www.mayoclinic.org/diseases-conditions/high-blood-cholesterol",
  },

  // ── Cardiovascular / Cardiac Markers ─────────────────────────────────────
  "10839-9": {
    description:
      "Troponin is a heart muscle protein released into the blood when cardiac injury occurs. Elevated levels require clinical context and urgent interpretation.",
    source: "American Heart Association",
    sourceUrl: "https://www.heart.org/en/health-topics/heart-attack/diagnosing-a-heart-attack",
  },
  "33762-6": {
    description:
      "BNP and NT-proBNP are hormones released when the heart is under strain. Higher levels can support evaluation of heart failure or volume overload.",
    source: "American Heart Association",
    sourceUrl: "https://www.heart.org/en/health-topics/heart-failure/diagnosing-heart-failure",
  },
  "8480-6": {
    description:
      "Systolic blood pressure is the pressure in arteries when the heart contracts. Elevated values increase cardiovascular and kidney risk over time.",
    source: "American Heart Association",
    sourceUrl: "https://www.heart.org/en/health-topics/high-blood-pressure",
  },
  "8462-4": {
    description:
      "Diastolic blood pressure is the pressure in arteries between heartbeats. Abnormal values are interpreted with systolic pressure and overall cardiovascular risk.",
    source: "American Heart Association",
    sourceUrl: "https://www.heart.org/en/health-topics/high-blood-pressure",
  },
  "8867-4": {
    description:
      "Heart rate measures how many times the heart beats per minute. Resting, sleeping, and activity values help show cardiovascular response and recovery patterns.",
    source: "American Heart Association",
    sourceUrl: "https://www.heart.org/en/healthy-living/fitness/fitness-basics/target-heart-rates",
  },
  "40443-4": {
    description:
      "Resting heart rate is measured when the body is at rest. Trends can reflect cardiovascular fitness, stress, illness, sleep, medications, or recovery status.",
    source: "American Heart Association",
    sourceUrl: "https://www.heart.org/en/healthy-living/fitness/fitness-basics/target-heart-rates",
  },
  "80404-7": {
    description:
      "Heart rate variability reflects beat-to-beat timing variation. Trends can provide context about recovery, autonomic balance, stress, and sleep quality.",
    source: "Cleveland Clinic",
    sourceUrl: "https://my.clevelandclinic.org/health/symptoms/21773-heart-rate-variability-hrv",
  },
  "59408-5": {
    description:
      "Blood oxygen saturation estimates the percentage of hemoglobin carrying oxygen. Low readings can reflect breathing, circulation, altitude, or measurement issues.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/pulse-oximetry/",
  },

  // ── Blood Cells / Hematology ─────────────────────────────────────────────
  "6690-2": {
    description:
      "White blood cells fight infection. Abnormal counts can indicate infection, immune disorders, or bone marrow problems.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/white-blood-count-wbc/",
  },
  "789-8": {
    description:
      "Red blood cells carry oxygen throughout the body. Low counts may indicate anemia; high counts may signal dehydration or other conditions.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/red-blood-cell-rbc-count/",
  },
  "718-7": {
    description:
      "Hemoglobin is the protein in red blood cells that carries oxygen. Low levels are common in anemia and iron deficiency.",
    source: "Mayo Clinic",
    sourceUrl: "https://www.mayoclinic.org/tests-procedures/hemoglobin-test",
  },
  "4544-3": {
    description:
      "Hematocrit is the percentage of blood volume made up by red blood cells. Low values can suggest anemia; high values can reflect dehydration or other conditions.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/hematocrit-test/",
  },
  "777-3": {
    description:
      "Platelets help blood clot after injury. Abnormal counts can increase bleeding risk, clotting risk, or suggest bone marrow or inflammatory conditions.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/platelet-tests/",
  },
  "28540-3": {
    description:
      "MCHC estimates the average hemoglobin concentration inside red blood cells. Abnormal values help classify types of anemia.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/red-blood-cell-rbc-indices/",
  },
  "785-6": {
    description:
      "MCH estimates the average amount of hemoglobin in each red blood cell. It is one of several red cell indices used to evaluate anemia patterns.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/red-blood-cell-rbc-indices/",
  },
  "787-2": {
    description:
      "MCV estimates average red blood cell size. Low or high values help distinguish different anemia patterns and nutrient deficiencies.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/red-blood-cell-rbc-indices/",
  },
  "788-0": {
    description:
      "RDW measures variation in red blood cell size. Higher values can support evaluation of anemia, nutritional deficiency, or mixed red cell populations.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/red-blood-cell-rbc-indices/",
  },
  "32623-1": {
    description:
      "Mean platelet volume estimates average platelet size. It can provide context about platelet production and turnover when interpreted with platelet count.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/platelet-tests/",
  },

  // ── Liver / Digestive ────────────────────────────────────────────────────
  "1742-6": {
    description:
      "ALT is a liver enzyme. Elevated levels may indicate liver damage, hepatitis, or fatty liver disease.",
    source: "American Liver Foundation",
    sourceUrl: "https://liverfoundation.org/about-your-liver/diagnosing-liver-disease",
  },
  "1920-8": {
    description:
      "AST is an enzyme found in the liver and heart. High levels can signal liver disease or muscle damage.",
    source: "American Liver Foundation",
    sourceUrl: "https://liverfoundation.org/about-your-liver/diagnosing-liver-disease",
  },
  "6768-6": {
    description:
      "ALP is an enzyme related to the bile ducts and bones. Elevated levels may indicate liver or bone disorders.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/alkaline-phosphatase/",
  },
  "1975-2": {
    description:
      "Bilirubin is a pigment made when red blood cells break down and processed by the liver. Elevated levels can cause jaundice and may reflect liver, bile duct, or blood disorders.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/bilirubin-blood-test/",
  },
  "1798-8": {
    description:
      "Amylase is an enzyme that helps digest carbohydrates. Elevated blood levels can occur with pancreatic or salivary gland inflammation.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/amylase-test/",
  },
  "3040-3": {
    description:
      "Lipase is a digestive enzyme made mainly by the pancreas. Elevated levels can support evaluation of pancreatitis or other pancreatic disease.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/lipase-tests/",
  },

  // ── Endocrine / Thyroid ──────────────────────────────────────────────────
  "11580-8": {
    description:
      "TSH regulates thyroid hormone production. High levels suggest hypothyroidism; low levels suggest hyperthyroidism.",
    source: "American Thyroid Association",
    sourceUrl: "https://www.thyroid.org/thyroid-function-tests",
  },
  "3016-3": {
    description:
      "TSH regulates thyroid hormone production. High levels suggest hypothyroidism; low levels suggest hyperthyroidism.",
    source: "American Thyroid Association",
    sourceUrl: "https://www.thyroid.org/thyroid-function-tests",
  },
  "3024-7": {
    description:
      "Free T4 measures thyroxine that is not bound to proteins and is available to tissues. It helps evaluate thyroid function alongside TSH.",
    source: "American Thyroid Association",
    sourceUrl: "https://www.thyroid.org/thyroid-function-tests",
  },
  "14920-3": {
    description:
      "Free T4 measures thyroxine that is not bound to proteins and is available to tissues. It helps evaluate thyroid function alongside TSH.",
    source: "American Thyroid Association",
    sourceUrl: "https://www.thyroid.org/thyroid-function-tests",
  },
  "3026-2": {
    description:
      "Total T4 measures thyroxine in the blood, including hormone bound to proteins. Results help evaluate thyroid function alongside TSH and free T4.",
    source: "American Thyroid Association",
    sourceUrl: "https://www.thyroid.org/thyroid-function-tests",
  },
  "3053-6": {
    description:
      "T3 is an active thyroid hormone. Abnormal levels can help characterize hyperthyroidism or other thyroid hormone imbalance.",
    source: "American Thyroid Association",
    sourceUrl: "https://www.thyroid.org/thyroid-function-tests",
  },
  "15067-2": {
    description:
      "FSH is a pituitary hormone involved in ovarian follicle development and sperm production. Levels vary by sex, age, and menstrual cycle stage.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/follicle-stimulating-hormone-fsh-levels-test/",
  },
  "10501-5": {
    description:
      "LH is a pituitary hormone involved in ovulation and testosterone production. Levels vary by sex, age, and menstrual cycle timing.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/luteinizing-hormone-lh-levels-test/",
  },
  "2842-3": {
    description:
      "Prolactin is a pituitary hormone involved in lactation and reproductive hormone signaling. Abnormal levels can reflect pituitary, thyroid, medication, or pregnancy-related factors.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/prolactin-levels/",
  },
  "2986-8": {
    description:
      "Total testosterone measures testosterone circulating in the blood. Interpretation depends on sex, age, symptoms, medications, and binding-protein levels.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/testosterone-levels-test/",
  },
  "2991-8": {
    description:
      "Free testosterone estimates the unbound testosterone fraction available to tissues. It can clarify androgen status when total testosterone and binding proteins differ.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/testosterone-levels-test/",
  },
  "2990-0": {
    description:
      "Free plus weakly bound testosterone estimates testosterone that is more available to tissues. It can add context when total testosterone is difficult to interpret.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/testosterone-levels-test/",
  },
  "2839-9": {
    description:
      "Progesterone is a reproductive hormone that rises after ovulation and supports pregnancy. Levels vary substantially by cycle phase and pregnancy status.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/progesterone-test/",
  },
  "2243-4": {
    description:
      "Estradiol is a major estrogen hormone. Levels vary by sex, age, menstrual cycle phase, pregnancy status, and hormone therapy.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/estrogen-levels-test/",
  },
  "38476-8": {
    description:
      "Anti-Mullerian hormone reflects ovarian follicle activity and is often used when evaluating ovarian reserve or fertility treatment response.",
    source: "Cleveland Clinic",
    sourceUrl:
      "https://my.clevelandclinic.org/health/diagnostics/22681-anti-mullerian-hormone-test",
  },
  "2191-5": {
    description:
      "DHEA-S is an adrenal androgen precursor. Abnormal levels can help evaluate adrenal function, androgen excess, or certain endocrine disorders.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/dhea-sulfate-test/",
  },
  "13967-5": {
    description:
      "SHBG is a liver-produced protein that binds sex hormones. Levels affect how much testosterone and estrogen are available to tissues.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/shbg-blood-test/",
  },
  "1989-3": {
    description:
      "Vitamin D supports bone health, immune function, and mood. Deficiency is common and linked to fatigue and weakened bones.",
    source: "Endocrine Society",
    sourceUrl: "https://www.endocrine.org/patient-engagement/endocrine-library/vitamin-d",
  },

  // ── Metabolism / Glucose ─────────────────────────────────────────────────
  "1558-6": {
    description:
      "Fasting glucose measures blood sugar after an overnight fast. Elevated levels may indicate prediabetes or diabetes.",
    source: "American Diabetes Association",
    sourceUrl: "https://diabetes.org/about-diabetes/diagnosis",
  },
  "4548-4": {
    description:
      "HbA1c reflects average blood sugar over 2–3 months. It's a key marker for diabetes management and risk.",
    source: "American Diabetes Association",
    sourceUrl: "https://diabetes.org/about-diabetes/a1c",
  },
  "2484-4": {
    description:
      "Insulin is the hormone that regulates blood sugar. High fasting levels can indicate insulin resistance.",
    source: "Cleveland Clinic",
    sourceUrl: "https://my.clevelandclinic.org/health/articles/22601-insulin",
  },

  // ── Inflammation ─────────────────────────────────────────────────────────
  "1988-5": {
    description:
      "CRP is a marker of inflammation. Elevated levels are associated with infection, chronic disease, or cardiovascular risk.",
    source: "American Heart Association",
    sourceUrl:
      "https://www.heart.org/en/health-topics/heart-attack/diagnosing-a-heart-attack/inflammation-and-heart-disease",
  },
  "5048-4": {
    description:
      "ANA testing looks for antinuclear antibodies that can occur in autoimmune diseases. Positive results are interpreted with symptoms and more specific antibody tests.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/ana-antinuclear-antibody-test/",
  },
  "30522-7": {
    description:
      "High-sensitivity CRP measures low-grade inflammation more precisely than standard CRP. It is often used with other risk factors when assessing cardiovascular risk.",
    source: "American Heart Association",
    sourceUrl:
      "https://www.heart.org/en/health-topics/heart-attack/diagnosing-a-heart-attack/inflammation-and-heart-disease",
  },
  "4537-7": {
    description:
      "ESR measures how quickly red blood cells settle in a tube. Higher values can reflect inflammation but are not specific to one condition.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/erythrocyte-sedimentation-rate-esr/",
  },
  "33959-8": {
    description:
      "Procalcitonin can rise with significant bacterial infection and sepsis. Results are interpreted with symptoms, exam findings, and other infection markers.",
    source: "Testing.com",
    sourceUrl: "https://www.testing.com/tests/procalcitonin/",
  },
  "26881-3": {
    description:
      "Interleukin-6 is an inflammatory cytokine involved in immune signaling. Elevated levels can reflect infection, autoimmune activity, or systemic inflammation.",
    source: "Testing.com",
    sourceUrl: "https://www.testing.com/tests/interleukin-6/",
  },
  "704-7": {
    description:
      "Absolute basophil count measures one type of white blood cell involved in allergic and inflammatory responses. Results are interpreted with the full blood count.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/blood-differential/",
  },
  "706-2": {
    description:
      "Basophil percentage shows the share of white blood cells that are basophils. It is interpreted with absolute counts and the rest of the differential.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/blood-differential/",
  },
  "711-2": {
    description:
      "Absolute eosinophil count measures eosinophils, a white blood cell type that can rise with allergies, asthma, parasites, or some inflammatory conditions.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/blood-differential/",
  },
  "713-8": {
    description:
      "Eosinophil percentage shows the share of white blood cells that are eosinophils. It is interpreted with absolute eosinophil count and symptoms.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/blood-differential/",
  },
  "731-0": {
    description:
      "Absolute lymphocyte count measures lymphocytes, white blood cells central to immune response. Abnormal values can occur with infection, immune conditions, or medications.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/blood-differential/",
  },
  "736-9": {
    description:
      "Lymphocyte percentage shows the share of white blood cells that are lymphocytes. It is interpreted alongside absolute lymphocyte count and total WBC.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/blood-differential/",
  },
  "742-7": {
    description:
      "Absolute monocyte count measures monocytes, white blood cells involved in inflammation and tissue cleanup. Results are interpreted with the full differential.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/blood-differential/",
  },
  "5905-5": {
    description:
      "Monocyte percentage shows the share of white blood cells that are monocytes. It is interpreted with absolute monocyte count and other immune markers.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/blood-differential/",
  },
  "770-8": {
    description:
      "Neutrophil percentage shows the share of white blood cells that are neutrophils. It helps evaluate infection or inflammation when interpreted with absolute neutrophil count.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/blood-differential/",
  },
  "38518-7": {
    description:
      "Immature granulocyte percentage can reflect early white blood cell forms in circulation. Elevations may occur with infection, inflammation, or bone marrow stimulation.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/blood-differential/",
  },
  "51584-1": {
    description:
      "Absolute immature granulocyte count measures early granulocyte forms. Results are interpreted with total WBC, differential findings, and clinical context.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/blood-differential/",
  },
  "8123-2": {
    description:
      "CD4 cells are helper T cells that coordinate immune responses. Counts are commonly used to assess immune status, especially in HIV care.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/cd4-lymphocyte-count/",
  },
  "20402-4": {
    description:
      "Natural killer cells are lymphocytes that help recognize and destroy infected or abnormal cells. Counts are interpreted with broader immune findings.",
    source: "National Cancer Institute",
    sourceUrl:
      "https://www.cancer.gov/publications/dictionaries/cancer-terms/def/natural-killer-cell",
  },
  "11572-5": {
    description:
      "Rheumatoid factor is an antibody that can be elevated in rheumatoid arthritis and other inflammatory or infectious conditions.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/rheumatoid-factor-rf-test/",
  },
  "53027-9": {
    description:
      "Anti-CCP antibodies are associated with rheumatoid arthritis and can help support diagnosis when interpreted with symptoms and exam findings.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/ccp-antibody-test/",
  },
  "11013-0": {
    description:
      "Anti-dsDNA antibodies are associated with systemic lupus erythematosus. Levels can support diagnosis and may track disease activity in some patients.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/anti-ds-dna/",
  },
  "56888-1": {
    description:
      "HIV antigen/antibody testing screens for HIV infection by detecting immune response and p24 antigen. Reactive results require confirmatory testing.",
    source: "CDC",
    sourceUrl: "https://www.cdc.gov/hiv/testing/index.html",
  },
  "5196-1": {
    description:
      "Hepatitis B surface antigen indicates current hepatitis B infection when present. Results are interpreted with other hepatitis B serologies.",
    source: "CDC",
    sourceUrl: "https://www.cdc.gov/hepatitis-b/testing/index.html",
  },
  "5198-7": {
    description:
      "Hepatitis C antibody testing screens for past or current hepatitis C exposure. Reactive results are usually followed by viral RNA testing.",
    source: "CDC",
    sourceUrl: "https://www.cdc.gov/hepatitis-c/testing/index.html",
  },
  "94500-6": {
    description:
      "COVID-19 PCR detects SARS-CoV-2 genetic material from a respiratory specimen and is used to identify current infection.",
    source: "CDC",
    sourceUrl: "https://www.cdc.gov/covid/testing/index.html",
  },

  // ── Kidney / Electrolytes ────────────────────────────────────────────────
  "2160-0": {
    description:
      "Creatinine is a waste product filtered by the kidneys. Elevated levels may indicate reduced kidney function.",
    source: "National Kidney Foundation",
    sourceUrl: "https://www.kidney.org/atoz/content/what-creatinine",
  },
  "62238-1": {
    description:
      "eGFR estimates how well the kidneys filter blood. Lower values can indicate reduced kidney function or chronic kidney disease.",
    source: "National Kidney Foundation",
    sourceUrl: "https://www.kidney.org/kidney-topics/estimated-glomerular-filtration-rate-egfr",
  },
  "3094-0": {
    description:
      "Blood urea nitrogen reflects urea waste from protein metabolism. Elevated values can occur with reduced kidney function, dehydration, or high protein breakdown.",
    source: "National Kidney Foundation",
    sourceUrl: "https://www.kidney.org/kidney-topics/blood-urea-nitrogen-bun-test",
  },
  "3097-3": {
    description:
      "BUN/creatinine ratio compares two kidney-related waste markers. It can help contextualize kidney function, hydration status, and protein metabolism.",
    source: "National Kidney Foundation",
    sourceUrl: "https://www.kidney.org/kidney-topics/blood-urea-nitrogen-bun-test",
  },
  "32294-1": {
    description:
      "Urine albumin-to-creatinine ratio detects albumin leakage into urine. Elevated values can be an early sign of kidney damage.",
    source: "National Kidney Foundation",
    sourceUrl: "https://www.kidney.org/kidney-topics/albuminuria-proteinuria",
  },
  "2161-8": {
    description:
      "Urine creatinine helps interpret urine concentration and is often used in ratios such as albumin-to-creatinine or protein-to-creatinine.",
    source: "National Kidney Foundation",
    sourceUrl: "https://www.kidney.org/kidney-topics/albuminuria-proteinuria",
  },
  "2888-6": {
    description:
      "Urine protein measures protein in urine. Persistent elevation can indicate kidney damage and should be interpreted with albumin, creatinine, and clinical context.",
    source: "National Kidney Foundation",
    sourceUrl: "https://www.kidney.org/kidney-topics/albuminuria-proteinuria",
  },
  "2823-3": {
    description:
      "Potassium is critical for heart rhythm and muscle function. Both high and low levels can be dangerous.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/potassium-blood-test/",
  },
  "2951-2": {
    description:
      "Sodium helps regulate fluid balance, blood pressure, and nerve function. Abnormal levels affect hydration and heart health.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/sodium-blood-test/",
  },
  "2028-9": {
    description:
      "CO₂ (bicarbonate) reflects the body's acid-base balance. Abnormal levels can indicate kidney or lung issues.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/carbon-dioxide-co2-in-blood/",
  },
  "2075-0": {
    description:
      "Chloride is an electrolyte that helps maintain fluid balance and acid-base status. Abnormal levels can occur with dehydration, kidney disease, or acid-base disorders.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/chloride-blood-test/",
  },
  "1963-8": {
    description:
      "Bicarbonate helps buffer blood acidity. Abnormal levels can reflect metabolic acid-base changes, kidney disease, or breathing-related compensation.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/carbon-dioxide-co2-in-blood/",
  },
  "33037-3": {
    description:
      "Anion gap is calculated from electrolyte values and helps evaluate acid-base disorders. High values can occur with metabolic acidosis from several causes.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/electrolyte-panel/",
  },
  "3084-1": {
    description:
      "Uric acid is a waste product from purine metabolism. High levels can cause gout and may indicate kidney problems.",
    source: "Mayo Clinic",
    sourceUrl: "https://www.mayoclinic.org/symptoms/high-uric-acid-level",
  },
  "33863-2": {
    description:
      "Cystatin C is a blood protein filtered by the kidneys. It can help estimate kidney function, sometimes alongside creatinine-based eGFR.",
    source: "National Kidney Foundation",
    sourceUrl: "https://www.kidney.org/kidney-topics/cystatin-c",
  },
  "5671-3": {
    description:
      "Lead measures exposure to the heavy metal lead. Elevated blood levels can affect the nervous system, blood, kidneys, and other organs.",
    source: "CDC",
    sourceUrl: "https://www.cdc.gov/lead-prevention/testing/index.html",
  },
  "5685-3": {
    description:
      "Mercury testing measures exposure to mercury. Elevated levels can affect the nervous system, kidneys, and other organ systems depending on exposure type.",
    source: "Testing.com",
    sourceUrl: "https://www.testing.com/tests/mercury/",
  },
  "5583-0": {
    description:
      "Arsenic testing measures exposure to arsenic. Elevated levels can occur from environmental, occupational, dietary, or water sources.",
    source: "Testing.com",
    sourceUrl: "https://www.testing.com/tests/arsenic/",
  },
  "3298-7": {
    description:
      "Acetaminophen levels help evaluate recent ingestion and risk of liver toxicity. Timing after ingestion is essential for interpretation.",
    source: "Testing.com",
    sourceUrl: "https://www.testing.com/tests/acetaminophen/",
  },
  "14334-7": {
    description:
      "Lithium levels monitor therapy and toxicity risk. The therapeutic window is narrow, so results are interpreted with dose timing, kidney function, and symptoms.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/lithium-blood-test/",
  },

  // ── Nutrients ────────────────────────────────────────────────────────────
  "1751-7": {
    description:
      "Albumin is a protein made by the liver. Low levels can indicate liver disease, kidney disease, or malnutrition.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/albumin-blood-test/",
  },
  "17861-6": {
    description:
      "Calcium is essential for bones, muscles, and nerves. Abnormal levels may point to parathyroid or bone disorders.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/calcium-blood-test/",
  },
  "1994-3": {
    description:
      "Ionized calcium measures the active, unbound calcium in blood. It can clarify calcium status when protein levels or acid-base balance affect total calcium.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/calcium-blood-test/",
  },
  "2284-8": {
    description:
      "Folate is a B vitamin needed for red blood cell production and DNA synthesis. Low levels can contribute to anemia and pregnancy-related neural tube risk.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/folate-test/",
  },
  "2498-4": {
    description:
      "Iron measures circulating iron available for red blood cell production. Results are commonly interpreted with ferritin, transferrin, and TIBC.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/iron-tests/",
  },
  "2276-4": {
    description:
      "Ferritin reflects stored iron in the body. Low levels suggest iron deficiency, while high levels can occur with inflammation, liver disease, or iron overload.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/ferritin-blood-test/",
  },
  "2132-9": {
    description:
      "Vitamin B12 supports nerve function and red blood cell production. Low levels can cause anemia, neuropathy, and cognitive or balance symptoms.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/vitamin-b-test/",
  },
  "5763-8": {
    description:
      "Zinc supports immune function, wound healing, taste, and many enzyme systems. Low levels can occur with poor intake or malabsorption.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/ency/article/002416.htm",
  },
  "1823-4": {
    description:
      "Vitamin E is a fat-soluble antioxidant. Low levels are uncommon but can occur with fat malabsorption and may affect nerves or muscles.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/ency/article/002406.htm",
  },
  "19123-9": {
    description:
      "Magnesium supports muscle, nerve, heart rhythm, and energy metabolism. Abnormal levels can occur with kidney disease, medications, or GI losses.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/magnesium-blood-test/",
  },
  "3034-6": {
    description:
      "Transferrin is the main blood protein that transports iron. Levels help evaluate iron status, inflammation, liver function, and nutrition.",
    source: "Testing.com",
    sourceUrl: "https://www.testing.com/tests/transferrin/",
  },
  "2500-7": {
    description:
      "Total iron-binding capacity estimates how much iron blood proteins can carry. It is commonly interpreted with iron, ferritin, and transferrin saturation.",
    source: "Testing.com",
    sourceUrl: "https://www.testing.com/tests/tibc/",
  },
  "2428-1": {
    description:
      "Homocysteine is an amino acid influenced by B vitamin status, genetics, kidney function, and other factors. Elevated levels can support nutritional or cardiovascular risk evaluation.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/homocysteine-test/",
  },
  "9322-9": {
    description:
      "Total cholesterol to HDL ratio compares overall cholesterol with HDL cholesterol. It can provide cardiovascular risk context alongside individual lipid values.",
    source: "American Heart Association",
    sourceUrl: "https://www.heart.org/en/health-topics/cholesterol",
  },

  // ── Activity / Body Metrics ──────────────────────────────────────────────
  "41981-2": {
    description:
      "Active calories estimate energy used during movement and exercise. Trends are most useful for comparing activity patterns over time.",
    source: "CDC",
    sourceUrl: "https://www.cdc.gov/physical-activity-basics/index.html",
  },
  "55423-8": {
    description:
      "Daily steps estimate walking and movement volume. Step trends can help track activity consistency, mobility, and changes in routine.",
    source: "CDC",
    sourceUrl: "https://www.cdc.gov/physical-activity-basics/index.html",
  },
  "55430-3": {
    description:
      "Distance tracks how far someone moves during walking, running, or daily activity. Trends are best interpreted alongside steps and workout context.",
    source: "CDC",
    sourceUrl: "https://www.cdc.gov/physical-activity-basics/index.html",
  },
  "29463-7": {
    description:
      "Body weight tracks total body mass. Trends can reflect changes in hydration, nutrition, body composition, medications, or metabolic health.",
    source: "CDC",
    sourceUrl: "https://www.cdc.gov/healthy-weight-growth/index.html",
  },
  "39156-5": {
    description:
      "BMI estimates body weight relative to height. It is a screening measure, not a direct measurement of body fat or metabolic health.",
    source: "CDC",
    sourceUrl: "https://www.cdc.gov/bmi/adult-calculator/bmi-categories.html",
  },
  "41982-0": {
    description:
      "Body fat percentage estimates how much body mass is fat tissue. Trends can help contextualize weight changes, though device methods vary in accuracy.",
    source: "Cleveland Clinic",
    sourceUrl: "https://my.clevelandclinic.org/health/articles/4182-body-mass-index-bmi",
  },
  "8302-2": {
    description:
      "Body height is used with weight to calculate BMI and other body-size measures. Adult height is usually stable, so changes may reflect measurement differences.",
    source: "CDC",
    sourceUrl: "https://www.cdc.gov/bmi/adult-calculator/index.html",
  },

  // ── Bone / Musculoskeletal ───────────────────────────────────────────────
  "2777-1": {
    description:
      "Phosphorus works with calcium for strong bones and teeth. Imbalances may reflect kidney or parathyroid issues.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/phosphate-in-blood/",
  },
  "2731-8": {
    description:
      "PTH controls calcium and phosphorus levels. Abnormal values can indicate parathyroid disorders or vitamin D deficiency.",
    source: "Endocrine Society",
    sourceUrl: "https://www.endocrine.org/patient-engagement/endocrine-library/parathyroid-hormone",
  },
  "2695-5": {
    description:
      "Osteocalcin is a protein produced by bone cells. It's a marker of bone formation activity.",
    source: "Testing.com",
    sourceUrl: "https://www.testing.com/tests/bone-markers/",
  },
  "82523-2": {
    description:
      "CTx measures bone resorption (breakdown). Elevated levels may indicate increased bone loss or osteoporosis risk.",
    source: "Testing.com",
    sourceUrl: "https://www.testing.com/tests/bone-markers/",
  },
  "47255-5": {
    description:
      "P1NP is a marker of new bone formation. It's used to monitor osteoporosis treatment and bone metabolism.",
    source: "Testing.com",
    sourceUrl: "https://www.testing.com/tests/bone-markers/",
  },
  "2157-6": {
    description:
      "CK is an enzyme found in muscles and the heart. Elevated levels can indicate muscle injury or overexertion.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/creatine-kinase/",
  },
  "88334-8": {
    description:
      "Lean body mass estimates body weight excluding fat mass. It can help interpret changes in weight, strength, and body composition over time.",
    source: "Cleveland Clinic",
    sourceUrl: "https://my.clevelandclinic.org/health/articles/4182-body-mass-index-bmi",
  },
  "60842-2": {
    description:
      "VO2 max estimates the body's capacity to use oxygen during intense exercise. Higher values generally reflect better cardiorespiratory fitness.",
    source: "American Heart Association",
    sourceUrl: "https://www.heart.org/en/healthy-living/fitness/fitness-basics/target-heart-rates",
  },
  "2639-3": {
    description:
      "Myoglobin is a protein released when muscle is damaged. It's used to detect muscle injury and rhabdomyolysis.",
    source: "Testing.com",
    sourceUrl: "https://www.testing.com/tests/myoglobin/",
  },
  "2532-0": {
    description:
      "LDH is an enzyme involved in energy production. Elevated levels can indicate tissue damage or disease.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/lab-tests/lactate-dehydrogenase-ldh-test/",
  },
  "1764-0": {
    description:
      "Aldolase is an enzyme involved in sugar metabolism. Elevated levels may indicate muscle or liver disease.",
    source: "Testing.com",
    sourceUrl: "https://www.testing.com/tests/aldolase/",
  },

  // ── Sleep / Autonomic / Continuous Glucose ───────────────────────────────
  "93831-6": {
    description:
      "Sleep efficiency estimates the percentage of time in bed spent asleep. Lower values can indicate fragmented sleep, insomnia, or measurement noise.",
    source: "Sleep Foundation",
    sourceUrl: "https://www.sleepfoundation.org/how-sleep-works/sleep-efficiency",
  },
  "93832-4": {
    description:
      "Sleep duration estimates total time asleep. Trends help contextualize recovery, fatigue, mood, and cardiometabolic health.",
    source: "CDC",
    sourceUrl: "https://www.cdc.gov/sleep/about/index.html",
  },
  "9279-1": {
    description:
      "Respiratory rate measures breaths per minute. Trends can change with sleep, activity, fever, lung disease, anxiety, or recovery status.",
    source: "Cleveland Clinic",
    sourceUrl: "https://my.clevelandclinic.org/health/diagnostics/22528-respiratory-rate",
  },
  "8310-5": {
    description:
      "Body temperature reflects heat balance and can rise with fever, inflammation, exertion, or environmental exposure. Trends are more useful than isolated wearable readings.",
    source: "MedlinePlus — NIH",
    sourceUrl: "https://medlineplus.gov/ency/article/001982.htm",
  },
  "99504-3": {
    description:
      "Interstitial glucose from a CGM estimates glucose in fluid between cells. It tracks trends and variability but can lag behind blood glucose.",
    source: "American Diabetes Association",
    sourceUrl: "https://diabetes.org/advocacy/technology/continuous-glucose-monitors",
  },
};
